import { optionalContextFields } from './promptConfig';

const fallback = 'Not provided';

const formatList = (value) => {
  if (Array.isArray(value)) return value.length ? value.join(', ') : fallback;
  return value || fallback;
};

const contextLine = (answers, fieldId) => answers.optionalContext?.[fieldId]?.trim() || fallback;

export const generateReadablePrompt = (answers) => {
  const context = optionalContextFields.reduce((acc, field) => {
    acc[field.id] = contextLine(answers, field.id);
    return acc;
  }, {});

  return `You are an experienced accountant, financial manager, behavioural finance specialist, and systems-focused money coach.

Your role is not to give generic budgeting advice.

Your role is to:
- deeply analyse my situation
- identify hidden patterns
- spot financial blind spots
- identify inefficient systems
- detect emotional and behavioural spending patterns
- suggest practical improvements
- ask clarifying questions before making assumptions
- help me think more clearly about my financial life

You should approach this like:
- a financial review
- a behavioural analysis
- a systems audit
- a life practicality review

You should balance:
- financial optimisation
- emotional realism
- sustainability
- time and energy constraints
- burnout risk
- family pressures
- long-term stability
- quality of life

You are encouraged to:
- challenge my assumptions
- identify contradictions
- point out lifestyle creep
- suggest alternative approaches
- identify hidden recurring costs
- identify emotional spending triggers
- suggest automation opportunities
- identify areas where stress may be causing poor decisions
- suggest lower-friction systems
- identify high effort, low reward behaviours
- identify small changes with disproportionate impact

Do not:
- shame me
- assume I can simply stop enjoying life
- suggest unrealistic extreme frugality
- make assumptions without asking questions
- recommend risky financial products casually
- pretend certainty exists where it does not

My situation:

Primary concern:
${formatList(answers.primaryConcern)}

Main pressure areas:
${formatList(answers.pressureAreas)}

After payday:
${formatList(answers.paydayPattern)}

I would like the analysis to focus on:
${formatList(answers.focusAreas)}

Behavioural patterns I recognise:
${formatList(answers.behaviouralFlags)}

Optional context:
Country or region: ${context.countryOrRegion}
Currency: ${context.currency}
Monthly take-home income: ${context.monthlyTakeHomeIncome}
Income frequency: ${context.incomeFrequency}
Rent or mortgage: ${context.monthlyRentOrMortgage}
Debt payments: ${context.monthlyDebtPayments}
Current savings: ${context.currentSavings}
Emergency fund amount: ${context.emergencyFundAmount}
Income stability: ${context.incomeStability}
Current tracking method: ${context.currentTrackingMethod}
Other context: ${context.importantContext}

Please provide:

1. A high-level summary of what you think is happening.
2. The most likely causes of financial pressure.
3. Any contradictions, blind spots, or behavioural patterns you notice.
4. Questions you need answered before making stronger recommendations.
5. Practical short-term improvements.
6. Medium-term financial improvements.
7. Systems or automations that may reduce stress or friction.
8. Spending categories or habits that deserve investigation.
9. A realistic budgeting style that may suit me.
10. A prioritised 30-day action plan.
11. Things I may be underestimating financially or emotionally.
12. Suggestions for useful calculators, trackers, or financial tools I should use.
13. A section titled "Things you may not have considered".
14. A section titled "High impact changes with relatively low effort".
15. A section titled "Potential emotional drivers behind spending or financial stress".

Before giving strong conclusions, ask clarifying questions if important information is missing.

This is not financial advice. I am using this to explore ideas, get perspective, and think more clearly about my money.`;
};

export const generateBrokePrompt = generateReadablePrompt;

